import React, {useCallback, useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";

const normalizeTextValue = (value) => {
    if (value === undefined || value === null) {
        return "";
    }
    return String(value);
};

const ReadOnlyText = ({value}) => (
    <span className="block whitespace-pre-wrap text-sm text-gray-900">
        {value ?? ""}
    </span>
);

ReadOnlyText.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ReadOnlyText.defaultProps = {
    value: "",
};

const EditableTextarea = ({value, onCommit, placeholder, rows}) => {
    const initialValue = useRef(normalizeTextValue(value));
    const [internalValue, setInternalValue] = useState(initialValue.current);
    const isEditingRef = useRef(false);

    useEffect(() => {
        const normalized = normalizeTextValue(value);
        if (!isEditingRef.current) {
            initialValue.current = normalized;
            setInternalValue(normalized);
        }
    }, [value]);

    const commit = useCallback(() => {
        isEditingRef.current = false;
        const normalized = normalizeTextValue(internalValue);
        if (normalized === initialValue.current) {
            return;
        }
        initialValue.current = normalized;
        if (typeof onCommit === "function") {
            onCommit(normalized);
        }
    }, [internalValue, onCommit]);

    const handleChange = useCallback((event) => {
        isEditingRef.current = true;
        setInternalValue(normalizeTextValue(event?.target?.value));
    }, []);

    const handleKeyDown = useCallback((event) => {
        if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
            event.preventDefault();
            commit();
            if (event.currentTarget && typeof event.currentTarget.blur === "function") {
                event.currentTarget.blur();
            }
        }
    }, [commit]);

    return (
        <textarea
            className="w-full resize-none border-none bg-transparent p-0 text-sm text-gray-900 focus:outline-none focus:ring-0"
            rows={rows}
            value={internalValue}
            onChange={handleChange}
            onBlur={commit}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
        />
    );
};

EditableTextarea.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onCommit: PropTypes.func,
    placeholder: PropTypes.string,
    rows: PropTypes.number,
};

EditableTextarea.defaultProps = {
    value: "",
    onCommit: undefined,
    placeholder: "",
    rows: 2,
};

const DocumentBodyEditableTextarea = (props) => (
    props.isEditable
        ? (
            <EditableTextarea
                value={props.value}
                onCommit={props.onCommit}
                placeholder={props.placeholder}
                rows={props.rows}
            />
        )
        : (
            <ReadOnlyText value={props.value} />
        )
);

DocumentBodyEditableTextarea.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onCommit: PropTypes.func,
    placeholder: PropTypes.string,
    rows: PropTypes.number,
    isEditable: PropTypes.bool,
};

DocumentBodyEditableTextarea.defaultProps = {
    value: "",
    onCommit: undefined,
    placeholder: "",
    rows: 2,
    isEditable: true,
};

export default DocumentBodyEditableTextarea;
